import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, FileText, CheckCircle2, AlertTriangle, Info } from 'lucide-react';
import { Drawer } from '@/components/ui/Drawer';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { api } from '@/lib/api';
import { useT } from '@/store/i18n';

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  is_read: 0 | 1 | boolean;
  created_at: string;
  related_type?: string;
  related_id?: number;
  related_title?: string;
}

const TYPE_ICON: Record<string, { icon: React.ReactNode; tone: string }> = {
  question_approved: { icon: <CheckCircle2 className="w-4 h-4" />, tone: 'bg-emerald-100 text-emerald-700' },
  question_rejected: { icon: <AlertTriangle className="w-4 h-4" />, tone: 'bg-red-100 text-red-700' },
  question_pending: { icon: <FileText className="w-4 h-4" />, tone: 'bg-amber-100 text-amber-700' },
  default: { icon: <Info className="w-4 h-4" />, tone: 'bg-blue-100 text-blue-700' },
};

function useTimeAgo() {
  const t = useT();
  return function timeAgo(iso: string) {
    const then = new Date(iso).getTime();
    const diff = (Date.now() - then) / 1000;
    if (diff < 60) return t('time.just_now');
    if (diff < 3600) return `${Math.floor(diff / 60)} ${t('time.minutes_ago')}`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ${t('time.hours_ago')}`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} ${t('time.days_ago')}`;
    return new Date(iso).toLocaleDateString('th-TH', { day: 'numeric', month: 'short' });
  };
}

export function NotificationsDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const timeAgo = useTimeAgo();
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const r = await api<{ data?: { notifications?: Notification[] } }>('notifications/index.php?limit=20');
      return r.data?.notifications ?? [];
    },
    enabled: open,
  });

  const markRead = useMutation({
    mutationFn: (id: number | 'all') =>
      api('notifications/index.php', {
        method: 'PUT',
        // Backend expects { mark_all: true } or { notification_ids: number[] }
        json: id === 'all' ? { mark_all: true } : { notification_ids: [id] },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const items = list.data ?? [];
  const unread = items.filter((n) => !n.is_read).length;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={400}
      title={
        <span className="inline-flex items-center gap-2">
          {t('notif.title')}
          {unread > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-semibold">
              {unread}
            </span>
          )}
        </span>
      }
    >
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <span className="text-xs text-slate-500">{items.length} {t('units.items')}</span>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<Check className="w-3.5 h-3.5" />}
          disabled={unread === 0}
          onClick={() => markRead.mutate('all')}
        >
          {t('notif.mark_all_read')}
        </Button>
      </div>

      <div className="divide-y divide-slate-100">
        {list.isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="px-4 py-3"><Skeleton className="h-12 w-full" /></div>
          ))
        ) : items.length === 0 ? (
          <EmptyState title={t('notif.empty_title')} description={t('notif.empty_desc')} />
        ) : (
          items.map((n) => {
            const type = TYPE_ICON[n.type] ?? TYPE_ICON.default;
            return (
              <button
                key={n.id}
                type="button"
                onClick={() => !n.is_read && markRead.mutate(n.id)}
                className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-slate-50 transition-colors ${
                  !n.is_read ? 'bg-brand-50/30' : ''
                }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${type.tone}`}>
                  {type.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-semibold text-slate-900 leading-snug">
                      {n.title}
                    </div>
                    {!n.is_read && (
                      <span className="w-2 h-2 rounded-full bg-brand-600 flex-shrink-0 mt-1.5" aria-label={t('notif.unread_aria')} />
                    )}
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.message}</div>
                  {n.related_title && (
                    <div className="text-xs text-slate-500 italic mt-1 truncate">— {n.related_title}</div>
                  )}
                  <div className="text-[11px] text-slate-400 mt-1.5">{timeAgo(n.created_at)}</div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </Drawer>
  );
}
